import { PlainButton, Tooltip } from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useClipboard } from "@jugl-web/utils/hooks/useClipboard";
import { FC, useCallback, useEffect, useState } from "react";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";

type CopyableCodeSnippetProps = {
  value: string;
};

export const CopyableCodeSnippet: FC<CopyableCodeSnippetProps> = ({
  value,
}) => {
  const { t } = useTranslations();
  const { copyToClipboard } = useClipboard();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsCopied(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isCopied]);

  const handleCopy = useCallback(() => {
    copyToClipboard(value);
    setIsCopied(true);
  }, [copyToClipboard, value]);

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-solid border-dark-200 bg-grey-100 p-4">
      <pre className="grow">
        <code className="m-0 font-secondary text-sm leading-[140%] tracking-[0.14px] break-all whitespace-pre-wrap text-dark">
          {value}
        </code>
      </pre>
      <Tooltip
        renderTrigger={({ props, ref }) => (
          <PlainButton
            className={cx(
              "flex h-8 w-8 items-center justify-center rounded-lg bg-grey-200 transition-colors hover:bg-grey-300",
              isCopied && "bg-[#E8E0F4] hover:bg-[#E8E0F4]"
            )}
            onClick={handleCopy}
            ref={ref}
            {...props}
            disabled={isCopied}
          >
            {isCopied ? <CheckIcon /> : <CopyIcon />}
          </PlainButton>
        )}
      >
        {isCopied
          ? t({
              id: "common.copied",
              defaultMessage: "Copied",
            })
          : t({
              id: "common.copy",
              defaultMessage: "Copy",
            })}
      </Tooltip>
    </div>
  );
};
